import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FolderDto, GalleryDto, MemberRole, TenantDto, UpdateTenantBody, UserDto } from '@nusign/picflow-backend-types';
import {
  Observable,
  catchError,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  of,
  switchMap,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { GROWTHBOOK } from '../growthbook';
import { backoffRetry } from '../lib/backoff-retry';
import { capitalize } from '../lib/capitalize';
import { debounce } from '../lib/debounce';
import { getTenantGroups, getUserGroups } from '../lib/get-tenant-groups';
import { randomString } from '../lib/random-string';
import { sleep } from '../lib/sleep';
import { IGNORE_AUTH_ERRORS, TENANT_CONTEXT } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { DomainInfo, DomainInfoService } from './domain-info.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { OrganizationService } from './organization.service';
import { PermissionService, TenantPermissions } from './permission.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private growthBook = inject(GROWTHBOOK);
  private state = inject(StateService);
  private http = inject(HttpClient);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private config = inject(AppConfigService);
  private storage = inject(StorageService);
  private socketService = inject(SocketService);
  private auth = inject(AuthService);
  private organizationService = inject(OrganizationService);
  private router = inject(Router);
  private toasts = inject(ToastService);
  private domainInfoService = inject(DomainInfoService);
  private permissionService = inject(PermissionService);

  private socketWatcher = this.socketService.onSocketEvent.subscribe(async event => {
    if (!event) return;
    const activeTenant = this.state.activeTenant$?.value;
    if (event.name === 'tenant.updated') {
      const tenant = event.payload.tenant as TenantDto;
      if (tenant.id !== activeTenant?.id) return;
      this.clearAsync();
      tenant['storage'] = { active: BigInt(tenant['active_storage']) };
      Object.assign(activeTenant, event.payload.tenant);
      this.state.activeTenant$.next(activeTenant);
    }

    if (event.name === 'user-tenant.deleted') {
      const userId: string = event.payload.userTenant['user'];
      const tenantId: string = event.payload.userTenant['tenant'];

      const isCurrentUser = userId === this.state.user?.id;
      const isCurrentTenant = tenantId === activeTenant?.id;

      const isDuplicate = !!event.payload.userTenant['deleted_at']; // backend sends 2 events. One with deleted_at and one without

      if (isCurrentUser && isCurrentTenant && !isDuplicate) {
        await sleep(1000 * 2); // sleeping to avoid race condition where backend still returns the tenant we have been removed from
        this.toasts.add('You have been removed from this workspace', 'info');
        this.router.navigate(['/a', 'workspaces']);
        return await this.loadTenants(true);
      }
      if (isCurrentUser) {
        return await this.loadTenants(true);
      }
      if (isCurrentTenant) {
        this.observableLoader.forceDataRefresh('members');
      }
    }
  });

  private clearAsync = debounce(() => {
    this.observableLoader.remove('profile');
    this.loader.removeFuzzy('organization');
  }, 1000 * 5);

  private waitTillCanAccessTenant(tenant: TenantDto) {
    return this.http
      .patch(
        `${this.config.apiEndpoint}/tenants/${tenant.id}`,
        {},
        {
          context: new HttpContext().set(TENANT_CONTEXT, tenant.id).set(IGNORE_AUTH_ERRORS, true),
        }
      )
      .pipe(backoffRetry());
  }

  public async loadTenants(noCache = false): Promise<TenantDto[]> {
    if (noCache) {
      this.observableLoader.remove('profile');
    }
    try {
      const tokenData = await this.auth.getTokenData();
      if (!tokenData) return;
      const resp = await firstValueFrom(this.getProfileData());
      const tenants = resp.tenants;
      tenants.forEach(tenant => {
        tenant['storage'] = { active: BigInt(tenant['active_storage']) };
      });

      this.state.tenants = tenants.filter(t => !t.deleted_at);
      const defaultTenant = this.getDefaultTenant(true);
      if (defaultTenant) await this.setActiveTenant(defaultTenant);

      return tenants;
    } catch (err) {
      console.error(err);
      // eslint-disable-next-line no-debugger
      debugger;
      this.auth.logout();
    }
  }

  getDefaultTenant(forAutoSelect: boolean): TenantDto {
    if (forAutoSelect) {
      if (this.state.sharedWithMeActiveTenant$.value) return undefined;
      if (this.state.visitingTenant) return undefined;
      if (this.storage.get('activeSharedWithMeTenant') || this.storage.get('backupSharedWithMeTenantId')) return;
    }
    const storageTenantId = this.storage.get('activeTenantId') || this.storage.get('backupTenantId');
    if (storageTenantId) {
      const tenant = this.state.tenants?.find(t => t.id === storageTenantId);
      if (tenant) return tenant;
    }
    if (forAutoSelect) return;
    if (this.state.tenants?.length === 1) return this.state.tenants[0];
  }

  public async setActiveTenant(tenant: TenantDto) {
    this.state.sharedWithMeActiveTenant$.next(undefined);
    this.storage.remove('activeSharedWithMeTenant');
    this.storage.remove('backupSharedWithMeTenantId');

    const activeTenant = this.state.activeTenant$?.value;
    if (!tenant) {
      this.storage.remove('activeTenantId');
      this.storage.remove('backupTenantId');
      if (activeTenant) {
        this.loader.clear();
        this.observableLoader.clear();
        this.state.activeTenantRole = undefined;
        this.permissionService.setRolePermissions(undefined);
        this.state.activeTenant$.next(undefined);
      }

      this.growthBook.setAttributes({
        ...this.growthBook.getAttributes(),
        tenantId: undefined,
      });
      return;
    }

    if (activeTenant?.id === tenant.id) {
      this.state.activeTenant$.next(tenant); // updating reference
      return;
    }
    if (activeTenant) {
      this.loader.clear();
      this.observableLoader.clear();
    }

    const [resp] = await Promise.all([
      this.getCurrentTenant(tenant.id),
      firstValueFrom(this.permissionService.loadTenantBillingPermissions(tenant.id)),
    ]);

    this.state.activeTenantRole = resp.role;
    this.permissionService.setRolePermissions(resp.permissions);
    this.storage.set('activeTenantId', tenant.id, true);
    this.storage.set('backupTenantId', tenant.id);

    this.growthBook.setAttributes({
      ...this.growthBook.getAttributes(),
      tenantId: tenant.id,
      tenantRole: resp.role,
      $groups: [...getTenantGroups(tenant), ...getUserGroups(this.state.user)],
    });

    // This is temporary until all users have moved to the new feature flag system. Can be removed after asset tags is live
    if (this.storage.has('featureFlagOverrides')) {
      if (this.state.activeTenantRole === 'admin' || this.state.activeTenantRole === 'owner') {
        const overrides = this.storage.get<{ [key: string]: boolean }>('featureFlagOverrides');
        if (overrides.assetTags) {
          this.joinTenantGroup('asset-tags');
        }
      }
      this.storage.remove('featureFlagOverrides');
    }

    this.state.activeTenant$.next(tenant);
  }

  public async refreshTenant(): Promise<void> {
    await sleep(1000); // sleeping because the updating of storage used is async
    this.observableLoader.remove('profile');
    this.observableLoader.remove('members');
    this.observableLoader.remove('invites');
    await this.loadTenants();
  }

  public async updateActiveTenantLocal(params: Partial<TenantDto>) {
    if (!this.state.activeTenant$?.value) return;
    this.state.activeTenant$.next({ ...this.state.activeTenant$?.value, ...params });
  }

  public async updateTenant(params: UpdateTenantBody, tenant = this.state.activeTenant$?.value) {
    await lastValueFrom(
      this.http.patch(`${this.config.apiEndpoint}/tenants/${tenant.id}`, params, {
        context: new HttpContext().set(TENANT_CONTEXT, tenant.id),
      })
    );
    this.observableLoader.remove('profile');

    Object.assign(tenant, params); // can be removed once state dot activeTenant isn't used anymore

    const activeTenant = this.state.activeTenant$?.value;
    if (tenant.id === activeTenant?.id) {
      this.state.activeTenant$.next({ ...activeTenant, ...params });
    }

    await this.loadTenants();
  }

  public checkTenantPath(path: string) {
    return this.http.get(`${this.config.apiEndpoint}/tenants/path/${path}/validate`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private getProfileDataInternal(tries = 0): Observable<{
    tenants: TenantDto[];
    user: UserDto;
  }> {
    return this.http
      .get<{
        tenants: { data: TenantDto[] };
        user: UserDto;
      }>(`${this.config.apiEndpoint}/profile/current_user`, {
        context: new HttpContext().set(IGNORE_AUTH_ERRORS, true),
      })
      .pipe(
        map(resp => {
          if (!resp) return;
          return {
            user: resp.user,
            tenants: resp.tenants.data,
          };
        }),
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
          const userCreatedAgo = Date.now() - this.auth.clerk.user.createdAt.getTime();
          if (userCreatedAgo > 1000 * 60 * 5) {
            // if the user was created more than 5 minutes ago, we can assume that retrying won't help
            return throwError(() => err);
          }
          if (tries > 10) return throwError(() => err);
          return timer(250).pipe(switchMap(() => this.getProfileDataInternal(tries + 1)));
        })
      );
  }

  public getProfileData() {
    return this.observableLoader.get('profile', () => this.getProfileDataInternal());
  }

  private toSafePath(path: string) {
    return path
      .toLowerCase()
      .replace(/ /g, '-')
      .replace(/[^a-z0-9]/g, '');
  }

  private getAutoTenantPath(user: UserDto, domainInfo: DomainInfo) {
    if (domainInfo && !domainInfo.isEmailProvider) {
      if (domainInfo.organization?.name) {
        return this.toSafePath(domainInfo.organization.name).slice(0, 32);
      }
      const domain = user.email.split('@')[1];
      return domain.split('.')[0];
    }
    return this.toSafePath(user.email.split('@')[0]).slice(0, 32);
  }

  private getAutoTenantName(user: UserDto, domainInfo: DomainInfo) {
    if (domainInfo && !domainInfo.isEmailProvider) {
      if (domainInfo.organization?.name) return domainInfo.organization.name?.slice(0, 32);
      const domain = user.email.split('@')[1];
      return capitalize(domain.split('.')[0]).slice(0, 32);
    }

    const first_name = user.first_name || 'Unnamed';
    return `${first_name}'s Space`.slice(0, 32);
  }

  public async autoCreateTenant(user: UserDto, count = 0): Promise<TenantDto> {
    if (count > 5) throw new Error('Could not create tenant');

    const domain = user.email.split('@')[1];
    const domainInfo = await firstValueFrom(this.domainInfoService.getDomainInfo(domain));
    const targetPath = this.getAutoTenantPath(user, domainInfo);
    const path = count ? targetPath + `-${randomString(4)}` : targetPath;

    const name = this.getAutoTenantName(user, domainInfo);

    const pathValid = await firstValueFrom(this.checkTenantPath(path));
    if (!pathValid) return await this.autoCreateTenant(user, count + 1);

    const params = domainInfo?.organization ? { onboarding_checklist: { setWorkspaceName: true } } : {};
    try {
      return await this.createTenant(name, path, params);
    } catch {
      return await this.autoCreateTenant(user, count + 1);
    }
  }

  public getTenantFromOrgId(orgId: string): Observable<TenantDto> {
    return this.observableLoader.get(`tenant-${orgId}`, () => {
      return this.http.get<TenantDto>(`${this.config.apiEndpoint}/tenants/clerk/${orgId}`);
    });
  }

  private async getMyTenantFromOrgId(orgId: string): Promise<TenantDto> {
    const tenants = await this.loadTenants();
    return tenants.find(t => t.clerk_organization_id === orgId);
  }

  public waitTillOrgExistsInBackend(orgId: string, member = false) {
    return of(false)
      .pipe(
        tap(() => this.observableLoader.remove('profile')),
        switchMap(() =>
          from(this.getMyTenantFromOrgId(orgId)).pipe(
            switchMap(tenant => {
              if (member) {
                return timer(3000).pipe(map(() => tenant));
              } else {
                return this.waitTillCanAccessTenant(tenant).pipe(map(() => tenant));
              }
            })
          )
        ),
        switchMap(tenant => (tenant ? of(tenant) : throwError(() => 'Tenant not found')))
      )
      .pipe(
        backoffRetry(),
        catchError(() => of(undefined))
      );
  }

  private waitTillOrgDeletedInBackend(orgId: string) {
    return of(false)
      .pipe(
        tap(() => this.observableLoader.remove('profile')),
        switchMap(() => from(this.getMyTenantFromOrgId(orgId))),
        switchMap(tenant => (tenant ? throwError(() => 'Tenant still found') : of(true)))
      )
      .pipe(
        backoffRetry(),
        catchError(() => of(false))
      );
  }

  public async createTenant(
    name = 'Unnamed tenant',
    path = randomString(16).toLowerCase(),
    otherParams: Partial<UpdateTenantBody> = {}
  ): Promise<TenantDto> {
    await this.auth.init();
    const organization = await this.organizationService.createOrganization(name, path);
    if (!organization) return;
    const start = new Date();
    const tenant = await firstValueFrom(this.waitTillOrgExistsInBackend(organization.id));
    if (!tenant) return;
    const end = new Date();
    console.debug('Tenant took ' + (end.getTime() - start.getTime()) + 'ms to be created in backend and be accessible');
    if (Object.keys(otherParams).length) {
      try {
        await firstValueFrom(from(this.updateTenant(otherParams, tenant)));
      } catch (err) {
        console.log('Error updating tenant after creation', err);
      }
    }
    return tenant;
  }

  public shouldShowWorkspaceDialog(item: GalleryDto | FolderDto) {
    const tenantId = (item.tenant as TenantDto)?.id || (item.tenant as string);
    const activeTenant = this.state.activeTenant$.value;
    if (!activeTenant) return false;
    if (activeTenant.id !== tenantId) return false;
    if (this.state.activeTenantRole === 'member') return false;
    if (!activeTenant.name) return true;
    if (activeTenant.created_at * 1000 < new Date('2023-04-01').getTime()) return false;
    return !activeTenant.onboarding_checklist.setWorkspaceName;
  }

  public async deleteTenant(tenant: TenantDto) {
    const organization = await this.organizationService.getOrganization(tenant.clerk_organization_id);
    await organization.destroy();
    await firstValueFrom(this.waitTillOrgDeletedInBackend(tenant.clerk_organization_id));
    this.setActiveTenant(undefined);
  }

  private saveTenantgroups(groups: string[]) {
    const checklist = this.state.activeTenant$.value.onboarding_checklist;
    const newChecklist = { ...checklist };
    Object.keys(checklist).forEach(key => {
      if (key.startsWith('ff_group_')) delete newChecklist[key];
    });
    groups.forEach(group => {
      newChecklist[`ff_group_${group}`] = true;
    });
    return this.updateTenant({ onboarding_checklist: newChecklist });
  }

  public async joinTenantGroup(group: string) {
    const groups = getTenantGroups(this.state.activeTenant$.value);
    if (groups.includes(group)) return;
    groups.push(group);
    return this.saveTenantgroups(groups);
  }

  public async leaveTenantGroup(group: string) {
    const groups = getTenantGroups(this.state.activeTenant$.value);
    if (!groups.includes(group)) return;
    const newGroups = groups.filter(g => g !== group);
    return this.saveTenantgroups(newGroups);
  }

  private async getCurrentTenant(tenantId = this.state.activeTenant$?.value?.id) {
    return await lastValueFrom(
      this.http.get<{
        role: MemberRole;
        permissions: TenantPermissions | string[];
        tenant: TenantDto;
      }>(`${this.config.apiEndpoint}/profile/current_tenant`, {
        context: new HttpContext().set(TENANT_CONTEXT, tenantId),
      })
    );
  }

  public async pollForTenantChange(checkFn: (tenant: TenantDto) => boolean, tries = 0): Promise<TenantDto> {
    const { tenant } = await this.getCurrentTenant();
    if (checkFn(tenant)) return tenant;
    if (tries > 30) return;
    await sleep(1000);
    return await this.pollForTenantChange(checkFn, tries + 1);
  }
}
